import React, { FC, useState } from 'react';
import { SolutionsTabProps } from './SolutionsTabProps';
import { Stack } from '@mui/material';
import useTranslation from '../../hooks/useTranslation';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../store';
import { selectSolutionTypeModal } from '../../store/modals/selectors';
import {
  selectProjectToEdit,
  selectSolutions,
} from '../../store/projects/selectors';
import { modalsActions } from '../../store/modals/slice';
import { ProjectSolutionType } from '../../models/project';
import {
  addSolutions,
  editSolution,
  removeSolution,
} from '../../store/projects/actionWrapper';
import Tooltip from '@ingka/tooltip';
import { SOLUTION_TYPES_LIST_DIGITAL } from '../../utils/selectOptions';
import { SolutionTypesEnum } from '../../models/selectOptionsModels';
import Text from '@ingka/text';
import ListView, { ListViewItem } from '@ingka/list-view';
import Button from '@ingka/button';
import plus from '@ingka/ssr-icon/paths/plus';
import SolutionModal from '../SolutionModal';
import InlineMessage from '@ingka/inline-message';
import AddSolutionsBanner from '../AddSolutionsBanner';
import { selectUserRole } from '../../store/userData/selectors';

const SolutionsTab: FC<SolutionsTabProps> = ({
  disabledFields,
  setValue,
  error,
  hideTitle,
  hideInlineMessage,
}) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const isVisible = useTypedSelector(selectSolutionTypeModal);
  const currentProjectSolutions = useTypedSelector(selectSolutions);
  const project = useTypedSelector(selectProjectToEdit);
  const userRole = useTypedSelector(selectUserRole);
  const [selectedSolution, setSelectedSolution] = useState(null);
  // todo-ppm fix auto reset to default value in state
  const openAddSolutionModal = () => {
    dispatch(modalsActions.openSolutionTypeModal());
  };
  const handleEditSolution = (id) => {
    setSelectedSolution(
      currentProjectSolutions.find((solution) => solution.id === id),
    );

    openAddSolutionModal();
  };

  const onClose = () => {
    setSelectedSolution(null);
    dispatch(modalsActions.closeSolutionTypeModal());
  };
  const addSolutionHandler = (solutions: ProjectSolutionType[]) => {
    dispatch(addSolutions(solutions));
    if (setValue) {
      setValue('solutions', [...currentProjectSolutions, ...solutions]);
    }
    onClose();
  };
  const editSolutionHandler = (solution: Partial<ProjectSolutionType>) => {
    dispatch(editSolution(solution));
    if (setValue) {
      const updatedSolutions = currentProjectSolutions.map((s) =>
        s.id === solution.id ? { ...s, ...solution } : s,
      );
      setValue('solutions', updatedSolutions);
    }
    onClose();
  };
  const removeSolutionHandler = (solution: Partial<ProjectSolutionType>) => {
    dispatch(removeSolution(solution.id));
    if (setValue) {
      const updatedSolutions = currentProjectSolutions.filter(
        (s) => s.id !== solution.id,
      );
      setValue('solutions', updatedSolutions);
    }
  };

  const isSolutionDeleteDisabled = (solution: Partial<ProjectSolutionType>) => {
    if (!currentProjectSolutions || !userRole) {
      return true;
    }

    if (!project) {
      return false;
    }

    if (
      disabledFields?.includes('ALL') ||
      disabledFields?.includes('solutions')
    ) {
      return !solution?.id.includes('unsaved-');
    }

    if (!project?.realEstateSISAN) {
      if (currentProjectSolutions.length <= 1) {
        return true;
      }
      if (solution?.id.includes('unsaved-')) {
        return false;
      }
      if (project?.isep) {
        return userRole !== 'EXP_SUPER_ADMIN';
      }
      return false;
    }

    return true;
  };

  return (
    <>
      {currentProjectSolutions.some((solution) =>
        SOLUTION_TYPES_LIST_DIGITAL.includes(
          solution.solutionType as SolutionTypesEnum,
        ),
      ) &&
        !hideInlineMessage && (
          <InlineMessage
            body={t(
              'flight.createSubmissionModal.capacity_info_solution_types',
            )}
            variant={'cautionary'}
          />
        )}

      <Stack pb={2} spacing={3}>
        {!hideTitle && (
          <Text textStyle="Heading.XS" tagName="h4">
            {t('tabs.solutions')}
          </Text>
        )}
        {currentProjectSolutions.length === 0 && (
          <Stack spacing={2}>
            <Text textStyle="Body.M" tagName="p">
              {t('message.noSolutions')}
            </Text>
          </Stack>
        )}
        <Button
          style={{ maxWidth: '200px' }}
          type={'secondary'}
          size={'xsmall'}
          onClick={openAddSolutionModal}
          ssrIcon={plus}
          disabled={disabledFields?.includes('ALL')}
        >
          Add Solution
        </Button>
        {currentProjectSolutions.length > 0 && (
          <ListView id="solution-items-list" showDivider size="medium">
            {currentProjectSolutions.map((solution) => (
              <Stack direction={'row'} alignItems={'center'}>
                <ListViewItem
                  key={Math.random()}
                  emphasised={true}
                  description={t(
                    `solutionCategories.${solution.solutionCategory}`,
                  )}
                  title={t(`solutionTypes.${solution.solutionType}`) || ''}
                  quantityLabel={
                    <>
                      <Stack direction={'row'}>
                        <Stack mr={1}>
                          {currentProjectSolutions.length <= 1 ? (
                            <Tooltip
                              tooltipText={t(
                                'tooltips.atLeastOneSolutionRequired',
                              )}
                              describedById={`tooltip-${solution.id}`}
                              position={'leading'}
                            >
                              <Button
                                text={'Delete'}
                                type={'secondary'}
                                size={'xsmall'}
                                onClick={() => removeSolutionHandler(solution)}
                                disabled={isSolutionDeleteDisabled(solution)}
                              />
                            </Tooltip>
                          ) : (
                            <Button
                              text={'Delete'}
                              type={'secondary'}
                              size={'xsmall'}
                              onClick={() => removeSolutionHandler(solution)}
                              disabled={isSolutionDeleteDisabled(solution)}
                            />
                          )}
                        </Stack>
                        <Button
                          text={'Edit'}
                          size={'xsmall'}
                          type={'primary'}
                          onClick={() => handleEditSolution(solution.id)}
                        />
                      </Stack>
                    </>
                  }
                />
              </Stack>
            ))}
          </ListView>
        )}
        {error && <AddSolutionsBanner />}

        <SolutionModal
          isVisible={isVisible}
          solution={selectedSolution}
          onClose={onClose}
          disabledFields={disabledFields}
          handleAddSolutions={addSolutionHandler}
          handleEditSolution={editSolutionHandler}
          unsavedSolutions={currentProjectSolutions.filter((solution) =>
            solution.id?.includes('unsaved-'),
          )}
        />
      </Stack>
    </>
  );
};

export default SolutionsTab;
